import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Library,
  Search,
  UploadCloud,
  FolderInput,
  MoreHorizontal,
  Pencil,
  Trash2,
  FileVideo,
  RotateCcw,
  Info,
} from 'lucide-react';
import { Container, PageHeader, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useAssets,
  useFastChannelServices,
  useCreateAsset,
  useDeleteAsset,
  useRetryAsset,
  type Asset,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { formatBytes } from '@/pages/services/_shared/analytics-format';
import { useDebounce } from '../shared';
import { formatDurationShort } from './shared';
import { FileManagerDialog } from './FileManagerDialog';
import { UploadAssetDialog } from './UploadAssetDialog';
import { RenameAssetDialog } from './RenameAssetDialog';
import { AssetStatusPill } from './AssetStatusPill';
import { AssetDetailsDialog } from './AssetDetailsDialog';

const BASE = '/services/fastchannel';

export function ContentLibraryDashboard() {
  const navigate = useNavigate();
  const { serviceUuid } = useParams<{ serviceUuid: string }>();
  const { show } = useToast();

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300).trim().toLowerCase();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [renameAsset, setRenameAsset] = useState<Asset | null>(null);
  // Track by id so the open details dialog reflects live polling updates (stage /
  // progress / metadata) rather than a stale snapshot.
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Asset | null>(null);

  const { data: services = [] } = useFastChannelServices();
  const service = services.find((s) => s.id === serviceUuid);
  const { data: assets = [], isLoading } = useAssets(serviceUuid);

  // "Add from storage" maps an existing object into the library — no upload.
  const createAsset = useCreateAsset(serviceUuid ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Asset added' });
      trackFeatureAction('fastchannel', 'asset', 'create');
      setBrowseOpen(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not add asset' }),
  });

  const remove = useDeleteAsset(serviceUuid ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Asset deleted' });
      trackFeatureAction('fastchannel', 'asset', 'delete');
      setPendingDelete(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete asset' }),
  });

  const retry = useRetryAsset(serviceUuid ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Re-processing asset' }),
    onError: () => show({ tone: 'danger', title: 'Could not retry asset' }),
  });

  const rows = useMemo<Asset[]>(() => {
    if (!search) return assets;
    return assets.filter(
      (a) =>
        a.name?.toLowerCase().includes(search) ||
        a.object_key?.toLowerCase().includes(search) ||
        a.content_type?.toLowerCase().includes(search),
    );
  }, [assets, search]);

  const columns = useMemo<ColumnDef<Asset, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        // Let Name absorb the slack — duration/size/added are narrow by nature.
        size: 640,
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <FileVideo size={14} color="var(--vn-fg-muted)" />
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
              {row.original.name || row.original.object_key}
            </span>
          </span>
        ),
      },
      {
        id: 'duration',
        header: 'Duration',
        enableSorting: false,
        size: 110,
        cell: ({ row }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-muted)' }}>
            {row.original.duration_seconds > 0 ? formatDurationShort(row.original.duration_seconds) : '—'}
          </span>
        ),
      },
      {
        id: 'size',
        header: 'Size',
        enableSorting: false,
        size: 110,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>
            {row.original.size_bytes > 0 ? formatBytes(row.original.size_bytes) : '—'}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Added',
        enableSorting: false,
        size: 150,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {row.original.created_at ? dayjs(row.original.created_at).format('YYYY-MM-DD HH:mm') : '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        size: 110,
        cell: ({ row }) => (
          <AssetStatusPill
            status={row.original.status}
            stage={row.original.stage}
            progress={row.original.progress}
            error={row.original.error}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 56,
        cell: ({ row }) => {
          const a = row.original;
          return (
            <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Asset actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setDetailsId(a.id)}>
                    <Info size={14} /> Details
                  </DropdownMenuItem>
                  {a.status === 'failed' && (
                    <DropdownMenuItem onSelect={() => retry.mutate(a.id)}>
                      <RotateCcw size={14} /> Retry
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={() => setRenameAsset(a)}>
                    <Pencil size={14} /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem tone="danger" onSelect={() => setPendingDelete(a)}>
                    <Trash2 size={14} /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Library size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={BASE} onClick={(e) => { e.preventDefault(); navigate(BASE); }}>FastChannel</BreadcrumbItem>
            <BreadcrumbItem
              href={`${BASE}/${serviceUuid}`}
              onClick={(e) => { e.preventDefault(); navigate(`${BASE}/${serviceUuid}`); }}
            >
              {service?.service_name || 'Service'}
            </BreadcrumbItem>
            <BreadcrumbItem current>Content Library</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Content Library"
        description="A catalog of media assets. Upload files or map existing storage objects, then pick them as program sources."
        actions={
          <div style={{ display: 'inline-flex', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="secondary"
              startIcon={<FolderInput size={15} />}
              onClick={() => setBrowseOpen(true)}
            >
              Add from storage
            </Button>
            <Button startIcon={<UploadCloud size={15} />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
          </div>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Find assets by name, key or type"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 340 }}
          />
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : assets.length === 0 ? (
        <EmptyState
          icon={<Library size={20} />}
          title="No assets yet"
          description="Upload a media file or add one from a connected storage to start your library."
          actions={
            <div style={{ display: 'inline-flex', gap: 'var(--vn-space-2)' }}>
              <Button
                variant="secondary"
                startIcon={<FolderInput size={15} />}
                onClick={() => setBrowseOpen(true)}
              >
                Add from storage
              </Button>
              <Button startIcon={<UploadCloud size={15} />} onClick={() => setUploadOpen(true)}>
                Upload
              </Button>
            </div>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.id}
          emptyState="No assets match your search."
        />
      )}

      {serviceUuid && uploadOpen && (
        <UploadAssetDialog open onOpenChange={setUploadOpen} serviceUuid={serviceUuid} />
      )}

      {serviceUuid && browseOpen && (
        <FileManagerDialog
          open
          onOpenChange={setBrowseOpen}
          serviceUuid={serviceUuid}
          onSelect={(_url, selection) =>
            createAsset.mutate({
              storage_id: selection.storageId,
              object_key: selection.objectKey,
              size_bytes: selection.sizeBytes,
            })
          }
        />
      )}

      {serviceUuid && renameAsset && (
        <RenameAssetDialog
          open
          onOpenChange={(o) => !o && setRenameAsset(null)}
          serviceUuid={serviceUuid}
          asset={renameAsset}
        />
      )}

      {detailsId && assets.find((a) => a.id === detailsId) && (
        <AssetDetailsDialog
          open
          onOpenChange={(o) => !o && setDetailsId(null)}
          asset={assets.find((a) => a.id === detailsId)!}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete asset?"
        description={`This removes “${pendingDelete?.name || pendingDelete?.object_key}” from the library. The underlying storage file is not deleted. Schedules already pointing at its URL keep playing. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
